import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const CompanyContext = createContext(null);
const LS_KEY = "reyansh_active_company";

export function CompanyProvider({ children }) {
  const { user, hasRole } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(LS_KEY) || null);
  const [loading, setLoading] = useState(false);

  // Only users with finance/viewer/admin can see companies
  const canSeeCompanies = !!user && hasRole && hasRole("finance", "viewer");

  const refresh = useCallback(async () => {
    if (!canSeeCompanies) {
      setCompanies([]);
      return [];
    }
    setLoading(true);
    try {
      const { data } = await api.get("/finance/companies");
      setCompanies(data);
      if (data.length && (!activeId || !data.find((c) => c.id === activeId))) {
        const next = data[0].id;
        setActiveId(next);
        localStorage.setItem(LS_KEY, next);
      }
      if (data.length === 0) {
        setActiveId(null);
        localStorage.removeItem(LS_KEY);
      }
      return data;
    } catch (err) {
      // Silently ignore 403s — user just doesn't have the role
      if (err?.response?.status !== 403) {
        const url = err?.__failedUrl || err?.config?.url || "?";
        const status = err?.response?.status || err?.code || "network";
        toast.error(`Companies fetch failed · ${status}`, { description: `URL: ${url} · ${err?.message || ""}`.slice(0, 200), duration: 10000 });
      }
      setCompanies([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [canSeeCompanies, activeId]);

  useEffect(() => {
    if (canSeeCompanies) refresh();
    else setCompanies([]);
  }, [canSeeCompanies]);  // eslint-disable-line react-hooks/exhaustive-deps

  const setActive = useCallback((id) => {
    setActiveId(id);
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  }, []);

  const active = useMemo(
    () => companies.find((c) => c.id === activeId) || null,
    [companies, activeId]
  );

  // Memoize context value so consumers don't re-render on every parent render
  const value = useMemo(
    () => ({ companies, active, activeId, setActive, refresh, loading }),
    [companies, active, activeId, setActive, refresh, loading]
  );

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
